import { useState, useEffect } from 'react';
import { MessageCircle, Loader2, Timer, ShieldCheck, Users, ChevronRight, AlertCircle } from 'lucide-react';
import { msalInstance } from '../../msalConfig';
import logoBioconnection from '../../assets/logo-BC.png';
import clsx from 'clsx';

export function LoginPage({ onLoginSuccess }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useRedirect, setUseRedirect] = useState(true); // Start met redirect methode

  useEffect(() => {
    // Check if we're returning from a redirect
    const handleRedirectPromise = async () => {
      try {
        await msalInstance.initialize(); // Initialize MSAL first
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          await handleLoginSuccess(response.account);
        }
      } catch (err) {
        console.error('Redirect error:', err);
        setError('Login mislukt. Probeer het opnieuw.');
      }
    };

    if (msalInstance) {
      handleRedirectPromise();
    }
  }, []);

  const handleLoginSuccess = async (account) => {
    try {
      const backendRes = await fetch('/api/auth/ms-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ms_id: account.localAccountId,
          name: account.name,
          email: account.username
        })
      });

      const data = await backendRes.json(); // ← altijd eerst parsen

      if (!backendRes.ok) {
        // Specifieke whitelist-melding herkennen
        if (backendRes.status === 403 && data.detail?.startsWith("WHITELIST_DENIED")) {
          setError("Dit domein heeft geen toegang tot deze applicatie!");
        } else {
          setError("Inloggen mislukt: " + (data.detail || `code ${backendRes.status}`));
        }
        return;
      }

      // Login succesvol
      const user = {
        name: account.name,
        email: account.username,
        id: data.user_id,
        ms_id: account.localAccountId,
        is_admin: data.is_admin
      };

      onLoginSuccess(user);
    } catch (err) {
      console.error("Backend authentication error:", err);
      setError("Er ging iets mis bij het inloggen. Probeer het later opnieuw.");
    }
  };

  const handleLogin = async (method = 'popup') => {
    setIsLoading(true);
    setError(null);

    try {
      await msalInstance.initialize();
      
      const loginRequest = {
        scopes: ["user.read"],
        prompt: "select_account"
      };

      let response;
      
      if (method === 'redirect') {
        // Use redirect method - less likely to be blocked
        msalInstance.loginRedirect(loginRequest);
        return; // Don't setIsLoading(false) here as redirect will reload page
      } else {
        // Use popup method
        response = await msalInstance.loginPopup(loginRequest);
      }

      if (response?.account) {
        await handleLoginSuccess(response.account);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.name === 'BrowserAuthError' && err.errorCode === 'popup_window_error') {
        setError('Popup werd geblokkeerd. Probeer de redirect methode.');
        setUseRedirect(true);
      } else if (err.name === 'InteractionRequiredAuthError') {
        setError('Aanvullende verificatie vereist. Probeer opnieuw.');
      } else {
        setError('Login mislukt. Controleer je internetverbinding en probeer opnieuw.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-800 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-400/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-6xl">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="text-center space-y-8">
            {/* Compact logo for mobile */}
            <div className="flex justify-center">
              <img 
                src={logoBioconnection} 
                alt="BioConnection Logo" 
                className="h-24 w-auto object-contain"
              />
            </div>

            {/* Compact heading for mobile */}
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                Welkom bij Luna
                <span className="bg-gradient-to-r from-primary-600 via-bioconnection-red to-secondary-500 bg-clip-text text-transparent block font-extrabold">
                  de HR-assistent van BioConnection
                </span>
              </h2>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed px-4">
                Intelligente HR-ondersteuning met onze geavanceerde AI-assistent
              </p>
            </div>

            {/* Login Card for Mobile */}
            {error && (
              <div className="mx-4">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-red-700 dark:text-red-300 text-xs">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Simple Login Button - No card wrapper */}
            <div className="px-4">
              {!useRedirect ? (
                <LoginButton
                  onClick={() => handleLogin('popup')}
                  isLoading={isLoading}
                  variant="primary"
                >
                  <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                    <path d="M11.5 0H0v11.5h11.5V0z" fill="#F25022"/>
                    <path d="M23 0H11.5v11.5H23V0z" fill="#7FBA00"/>
                    <path d="M11.5 11.5H0V23h11.5V11.5z" fill="#00A4EF"/>
                    <path d="M23 11.5H11.5V23H23V11.5z" fill="#FFB900"/>
                  </svg>
                  Log in met Microsoft
                </LoginButton>
              ) : (
                <LoginButton
                  onClick={() => handleLogin('redirect')}
                  isLoading={isLoading}
                  variant="primary"
                >
                  <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                    <path d="M11.5 0H0v11.5h11.5V0z" fill="#F25022"/>
                    <path d="M23 0H11.5v11.5H23V0z" fill="#7FBA00"/>
                    <path d="M11.5 11.5H0V23h11.5V11.5z" fill="#00A4EF"/>
                    <path d="M23 11.5H11.5V23H23V11.5z" fill="#FFB900"/>
                  </svg>
                  Log in met Microsoft
                </LoginButton>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-8 items-center">
          {/* Left side - Desktop */}
          <div className="text-center lg:text-left space-y-1">
            {/* Large prominent logo */}
            <div className="flex justify-center lg:justify-start mb-8">
              <img 
                src={logoBioconnection} 
                alt="BioConnection Logo" 
                className="h-32 w-auto object-contain"
              />
            </div>

            {/* Main heading */}
            <div className="space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
                Welkom bij Luna
                <span className="bg-gradient-to-r from-primary-600 via-bioconnection-red to-secondary-500 bg-clip-text text-transparent block font-extrabold">
                  de HR-assistent van BioConnection
                </span>
              </h2>
              
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                Luna is jouw persoonlijke vraagbaak voor al je snelle HR-vragen.
                Stel al je vragen over zaken zoals verlof, verzuim en andere regelingen aan Luna.

              </p>
            </div>

            {/* Simple clean feature list */}
            {/* <div className="space-y-4">
              <div className="flex items-center gap-4 text-left">
                <div className="w-2 h-2 bg-bioconnection-red rounded-full"></div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Supersnel</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">— Direct antwoorden uit HR-documenten</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-left">
                <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Vertrouwelijk</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">— Veilige verwerking van personeelsgegevens</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-left">
                <div className="w-2 h-2 bg-secondary-500 rounded-full"></div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">HR-gericht</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">— Gespecialiseerd in personeelsvragen</span>
                </div>
              </div>
            </div> */}
          </div>

          {/* Right side - Desktop Login */}
          <div className="flex justify-center lg:justify-end">
            <div className="w-full max-w-md">
              <div className="glass dark:glass-dark rounded-3xl p-8 shadow-2xl border border-white/20 dark:border-white/10">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Welkom terug
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Log in om door te gaan
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                      <p className="text-red-700 dark:text-red-300 text-sm">
                        {error}
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {!useRedirect ? (
                    <LoginButton
                      onClick={() => handleLogin('popup')}
                      isLoading={isLoading}
                      variant="primary"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                        <path d="M11.5 0H0v11.5h11.5V0z" fill="#F25022"/>
                        <path d="M23 0H11.5v11.5H23V0z" fill="#7FBA00"/>
                        <path d="M11.5 11.5H0V23h11.5V11.5z" fill="#00A4EF"/>
                        <path d="M23 11.5H11.5V23H23V11.5z" fill="#FFB900"/>
                      </svg>
                      Log in met Microsoft
                    </LoginButton>
                  ) : (
                    <LoginButton
                      onClick={() => handleLogin('redirect')}
                      isLoading={isLoading}
                      variant="primary"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                        <path d="M11.5 0H0v11.5h11.5V0z" fill="#F25022"/>
                        <path d="M23 0H11.5v11.5H23V0z" fill="#7FBA00"/>
                        <path d="M11.5 11.5H0V23h11.5V11.5z" fill="#00A4EF"/>
                        <path d="M23 11.5H11.5V23H23V11.5z" fill="#FFB900"/>
                      </svg>
                      Log in om door te gaan
                    </LoginButton>
                  )}

                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                    
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, color = 'blue' }) {
  const colorClasses = {
    indigo: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/20',
    emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/20',
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/20',
    violet: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/20'
  };

  return (
    <div className="glass dark:glass-dark rounded-xl p-6 border border-white/20 dark:border-white/10 hover:shadow-lg transition-all duration-300">
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white text-base mb-1">
            {title}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function LoginButton({ onClick, isLoading, children, variant = 'primary' }) {
  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={clsx(
        "w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg",
        variant === 'primary' && "bg-gradient-to-r from-bioconnection-red to-secondary-500 hover:from-primary-700 hover:to-secondary-600 text-white shadow-primary-500/25",
        isLoading && "opacity-70 cursor-not-allowed transform-none"
      )}
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          {children}
          <ChevronRight className="w-5 h-5" />
        </>
      )}
    </button>
  );
}
